import { ConditionalFunctionString } from '../../conditions.types'
import { parseAmount } from '../runtimeHelpers'

/**
 * Payment terms are a day count on the quote (`defaultDueDateDays`). A quote
 * that leaves them unset inherits the account default, which the workflow never
 * sees, so these three can only answer for terms a quote states itself and an
 * unset value satisfies none of them. The guard also stops `parseAmount(null)`
 * (which is 0) reading as "less than 7 days".
 *
 * `parseAmount` is borrowed from the amount comparisons purely to coerce
 * whatever the quote reports into a number; nothing here is a currency.
 */
const buildPaymentTermsComparison =
  (operator: '<' | '>' | '==='): ConditionalFunctionString =>
  ({ property, value }) =>
    `${property} != null && ${parseAmount(property)} ${operator} ${Number(value)}`

export const paymentTermsLessThan = buildPaymentTermsComparison('<')
export const paymentTermsMoreThan = buildPaymentTermsComparison('>')
export const paymentTermsEquals = buildPaymentTermsComparison('===')

/**
 * Guards the other way round, because an unset value is one of the answers:
 * terms that aren't 30 days include terms the quote never set.
 */
export const paymentTermsNotEquals: ConditionalFunctionString = ({
  property,
  value
}) => `${property} == null || ${parseAmount(property)} !== ${Number(value)}`

/**
 * Asks about the absence itself, which is how a workflow reaches quotes that
 * take the account default. It's the only operator that needs no day count.
 */
export const paymentTermsIsNotSet: ConditionalFunctionString = ({ property }) =>
  `${property} == null`
